/**
 * Tutorial guiado — DOM: overlay, spotlight, balão, posicionamento.
 * Lógica de progresso fica em tutorial-core.mjs.
 */
import {
  markAllPhasesDone,
  markStepDone,
  maybeStartNextPhase,
  nextStep,
  normalizeTutorial,
  phaseProgress,
  restartTutorialProgress,
  setCurrentStep,
  shouldMigrateVeteran,
  skipTutorial,
  skippedByLayout,
} from './tutorial-core.mjs?v=20260727-atlas-guide-3';

const PAD = 6;
/** Recorte mínimo do holofote, para alvos pequenos como o toggle 29×16. */
const MIN_HOLE = 40;
/** Espera pelo alvo aparecer. Enquanto espera, o jogo NÃO é bloqueado. */
const TARGET_WAIT_MS = 4000;
/** Alvo encolheu para 0×0 (painel fechou) — descarta o passo. */
const TARGET_GONE_MS = 1500;
/** Passo de clique/evento parado tempo demais: libera "Próximo" como saída. */
const ESCAPE_HATCH_MS = 8000;
/** Reescalar rolagem no máximo a cada X ms, para não brigar com o usuário. */
const RESCROLL_MS = 600;
const Z_INDEX = 9500;

/** @type {null | {
 *  getState: () => object,
 *  save: () => void,
 *  t: (key: string, vars?: object) => string,
 *  switchView: (name: string) => void,
 *  openPanel: (id: string) => void,
 *  isBusy: () => boolean,
 *  showToast: (msg: string) => void,
 *  currentView?: () => string,
 *  isHunting?: () => boolean,
 *  prepareStep?: (id: string) => void,
 * }} */
let hooks = null;

let root = null;
let blockers = null;
let ring = null;
let balloon = null;
let beak = null;
let titleEl = null;
let bodyEl = null;
let progressEl = null;
let trackFill = null;
let nextBtn = null;
let skipBtn = null;
let confirmBox = null;

let activeStep = null;
let activeTarget = null;
let rafId = 0;
let lastRectKey = '';
let waitingSince = 0;
let goneSince = 0;
let lastScrollAt = 0;
let firstPaint = true;
let paintedOnce = false;
let queued = false;
let hatchTimer = 0;
let retryTimer = 0;
let visibilityBound = false;
let clickBound = false;
/** Passos descartados nesta sessão — não viram `done`, voltam no próximo login. */
const sessionSkipped = new Set();

function stateTutorial() {
  const state = hooks.getState();
  state.tutorial = normalizeTutorial(state.tutorial);
  return state;
}

function persist(tutorial) {
  const state = hooks.getState();
  state.tutorial = normalizeTutorial(tutorial);
  hooks.save();
}

function ensureDom() {
  if (root) return;
  root = document.createElement('div');
  root.id = 'tutorialOverlay';
  root.className = 'tutorial-overlay hidden';
  root.dataset.mode = 'waiting';
  root.setAttribute('aria-hidden', 'true');
  root.innerHTML = `
    <div class="tutorial-blocker" data-edge="top"></div>
    <div class="tutorial-blocker" data-edge="bottom"></div>
    <div class="tutorial-blocker" data-edge="left"></div>
    <div class="tutorial-blocker" data-edge="right"></div>
    <div class="tutorial-ring" aria-hidden="true"></div>
    <div class="tutorial-balloon" role="dialog" aria-modal="true" aria-live="polite" data-place="center">
      <span class="tutorial-beak" aria-hidden="true"></span>
      <div class="tutorial-balloon-head">
        <strong class="tutorial-title"></strong>
        <span class="tutorial-progress"></span>
      </div>
      <p class="tutorial-body"></p>
      <div class="tutorial-track" aria-hidden="true"><i></i></div>
      <div class="tutorial-actions">
        <button type="button" class="tutorial-skip"></button>
        <button type="button" class="tutorial-next"></button>
      </div>
      <div class="tutorial-confirm hidden">
        <p class="tutorial-confirm-text"></p>
        <div class="tutorial-confirm-actions">
          <button type="button" class="tutorial-confirm-no"></button>
          <button type="button" class="tutorial-confirm-yes"></button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(root);
  blockers = {
    top: root.querySelector('[data-edge="top"]'),
    bottom: root.querySelector('[data-edge="bottom"]'),
    left: root.querySelector('[data-edge="left"]'),
    right: root.querySelector('[data-edge="right"]'),
  };
  ring = root.querySelector('.tutorial-ring');
  balloon = root.querySelector('.tutorial-balloon');
  beak = root.querySelector('.tutorial-beak');
  titleEl = root.querySelector('.tutorial-title');
  bodyEl = root.querySelector('.tutorial-body');
  progressEl = root.querySelector('.tutorial-progress');
  trackFill = root.querySelector('.tutorial-track > i');
  nextBtn = root.querySelector('.tutorial-next');
  skipBtn = root.querySelector('.tutorial-skip');
  confirmBox = root.querySelector('.tutorial-confirm');

  nextBtn.addEventListener('click', () => {
    if (!activeStep) return;
    completeActiveStep();
  });
  skipBtn.addEventListener('click', () => openSkipConfirm());
  confirmBox.querySelector('.tutorial-confirm-no').addEventListener('click', () => closeSkipConfirm());
  confirmBox.querySelector('.tutorial-confirm-yes').addEventListener('click', () => doSkip());
  document.addEventListener('keydown', onKeyDown);
  window.addEventListener('resize', () => { lastRectKey = ''; }, { passive: true });
}

function onKeyDown(event) {
  if (!activeStep || root?.classList.contains('hidden')) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    if (confirmBox.classList.contains('hidden')) openSkipConfirm();
    else closeSkipConfirm();
    return;
  }
  if (event.key !== 'Tab' || !balloon) return;
  const focusables = [...balloon.querySelectorAll('button')]
    .filter((el) => !el.disabled && el.offsetParent !== null);
  if (!focusables.length) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function openSkipConfirm() {
  confirmBox.querySelector('.tutorial-confirm-text').textContent = hooks.t('tut.skipConfirm');
  confirmBox.querySelector('.tutorial-confirm-no').textContent = hooks.t('tut.skipNo');
  confirmBox.querySelector('.tutorial-confirm-yes').textContent = hooks.t('tut.skipYes');
  confirmBox.classList.remove('hidden');
  balloon.classList.add('is-confirming');
  confirmBox.querySelector('.tutorial-confirm-no').focus();
}

function closeSkipConfirm() {
  confirmBox.classList.add('hidden');
  balloon.classList.remove('is-confirming');
  skipBtn.focus();
}

function doSkip() {
  clearHatch();
  stopTracking();
  hideOverlay();
  closeSkipConfirm();
  persist(skipTutorial(stateTutorial().tutorial));
  activeStep = null;
  activeTarget = null;
}

function bindVisibility() {
  if (visibilityBound) return;
  visibilityBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopTracking();
    else if (activeStep) { lastRectKey = ''; startTracking(); }
  });
}

function bindClickAdvance() {
  if (clickBound) return;
  clickBound = true;
  document.addEventListener('click', (event) => {
    if (!hooks || !activeStep) return;
    if (activeStep.advance?.type !== 'click' && activeStep.advance?.type !== 'any') return;
    if (!activeTarget) return;
    if (root && root.contains(event.target)) return;
    if (!activeTarget.contains(event.target) && event.target !== activeTarget) return;
    const stepId = activeStep.id;
    queueMicrotask(() => {
      if (activeStep?.id === stepId) completeActiveStep();
    });
  }, true);
}

/**
 * Sobe até o ancestral com caixa visível — inputs de toggle são `display:none`
 * e quem tem tamanho é o `<label class="switch">` em volta.
 * Para no `body`: se chegou lá, o alvo está dentro de um painel fechado e
 * NÃO existe agora (destacar a página inteira seria pior que não destacar).
 */
function promoteToVisible(el) {
  const limiteArea = window.innerWidth * window.innerHeight * 0.6;
  let node = el;
  let hops = 0;
  while (node && node !== document.body && node !== document.documentElement && hops < 5) {
    const rect = node.getBoundingClientRect();
    if (rect.width >= 2 && rect.height >= 2) {
      // Um ancestral que cobre quase a tela toda não é um alvo de holofote.
      return rect.width * rect.height > limiteArea ? null : node;
    }
    node = node.parentElement;
    hops += 1;
  }
  return null;
}

function resolveTarget(step) {
  if (!step?.target) return null;
  if (typeof step.target === 'function') {
    try {
      const el = step.target();
      return el ? promoteToVisible(el) : null;
    } catch { return null; }
  }
  const selector = String(step.target);
  let found = null;
  try {
    if (step.targetMatch === 'first-enabled') {
      const nodes = [...document.querySelectorAll(selector)];
      found = nodes.find((el) => {
        if (el.disabled || el.getAttribute('aria-disabled') === 'true') return false;
        if (el.classList.contains('locked') || el.classList.contains('disabled')) return false;
        const style = getComputedStyle(el);
        return style.visibility !== 'hidden';
      }) || null;
    } else {
      found = document.querySelector(selector);
    }
  } catch (error) {
    console.debug('[tutorial] seletor inválido', step.id, selector, error);
    return null;
  }
  return found ? promoteToVisible(found) : null;
}

/** Traz o alvo para dentro da viewport e do contêiner rolável (nav rola no eixo X). */
function scrollTargetIntoView(el) {
  const now = performance.now();
  if (now - lastScrollAt < RESCROLL_MS) return;
  lastScrollAt = now;
  try {
    el.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
  } catch { /* ignore */ }
}

function isOffscreen(rect) {
  return rect.bottom < 0 || rect.top > window.innerHeight
    || rect.right < 0 || rect.left > window.innerWidth;
}

/**
 * O alvo está atrás de outra coisa? Iluminar um elemento coberto por um modal
 * produz um recorte preto sem sentido — foi o que aconteceu com os passos da
 * hunt enquanto o Atlas estava aberto por cima.
 */
function isOccluded(el, rect) {
  const cx = Math.round(rect.left + rect.width / 2);
  const cy = Math.round(rect.top + rect.height / 2);
  if (cx < 0 || cy < 0 || cx > window.innerWidth || cy > window.innerHeight) return false;
  const hit = document.elementFromPoint(cx, cy);
  if (!hit) return true;
  // Nosso próprio overlay não conta como oclusão (o buraco fica sobre o alvo).
  if (root && root.contains(hit)) return false;
  return !(hit === el || el.contains(hit) || hit.contains(el));
}

/**
 * Garante o contexto do passo. Retorna false quando o passo deve esperar
 * (ex.: exige a cidade, mas o jogador está no meio de uma caça).
 */
function ensureContext(step) {
  if (!step?.ensure) return true;
  if (step.ensure === 'city' || step.ensure === 'adventure') {
    const view = hooks.currentView?.();
    if (view === step.ensure) return true;
    // Nunca cortar a caça do jogador para mostrar um passo.
    if (step.ensure === 'city' && hooks.isHunting?.()) return false;
    try { hooks.switchView(step.ensure); } catch (error) {
      console.debug('[tutorial] switchView falhou', step.id, error);
    }
    return true;
  }
  try { hooks.openPanel(step.ensure); } catch (error) {
    console.debug('[tutorial] openPanel falhou', step.id, error);
  }
  return true;
}

function rectKey(rect) {
  return [rect.top, rect.left, rect.width, rect.height, window.innerWidth, window.innerHeight]
    .map((n) => Math.round(n)).join(':');
}

function setMode(mode) {
  if (root.dataset.mode !== mode) root.dataset.mode = mode;
}

function applySpotlight(rect) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Alvos minúsculos (toggle 29×16) ganham um recorte mínimo para o holofote ler bem.
  const padX = Math.max(PAD, (MIN_HOLE - rect.width) / 2);
  const padY = Math.max(PAD, (MIN_HOLE - rect.height) / 2);
  const top = Math.max(0, rect.top - padY);
  const left = Math.max(0, rect.left - padX);
  const right = Math.min(vw, rect.right + padX);
  const bottom = Math.min(vh, rect.bottom + padY);
  const width = Math.max(0, right - left);
  const height = Math.max(0, bottom - top);

  blockers.top.style.cssText = `top:0;left:0;width:100%;height:${top}px;`;
  blockers.bottom.style.cssText = `top:${bottom}px;left:0;width:100%;height:${Math.max(0, vh - bottom)}px;`;
  blockers.left.style.cssText = `top:${top}px;left:0;width:${left}px;height:${height}px;`;
  blockers.right.style.cssText = `top:${top}px;left:${right}px;width:${Math.max(0, vw - right)}px;height:${height}px;`;

  ring.classList.toggle('is-animated', firstPaint);
  ring.style.top = `${top}px`;
  ring.style.left = `${left}px`;
  ring.style.width = `${width}px`;
  ring.style.height = `${height}px`;
  ring.classList.remove('hidden');
  setMode('spotlight');
  positionBalloon(rect, activeStep?.placement || 'bottom');
  firstPaint = false;
  paintedOnce = true;
}

/** Sem alvo: modal (bloqueia) ou espera (não bloqueia, jogo segue jogável). */
function clearSpotlightHole(mode) {
  const vh = window.innerHeight;
  blockers.top.style.cssText = `top:0;left:0;width:100%;height:${vh}px;`;
  blockers.bottom.style.cssText = 'display:none;';
  blockers.left.style.cssText = 'display:none;';
  blockers.right.style.cssText = 'display:none;';
  ring.classList.add('hidden');
  setMode(mode);
  positionBalloon(null, 'center');
}

function positionBalloon(rect, placement) {
  const margin = 12;
  const bw = balloon.offsetWidth || 300;
  const bh = balloon.offsetHeight || 160;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const mobileNav = vw <= 720 ? 64 : 0;
  const gap = PAD + 12;
  const clampLeft = (left) => Math.min(Math.max(margin, left), Math.max(margin, vw - bw - margin));
  const clampTop = (top) => Math.min(
    Math.max(margin, top),
    Math.max(margin, vh - bh - margin - mobileNav),
  );

  const tryPlace = (place) => {
    if (!rect) return null;
    // Em telas estreitas, alvos perto das bordas ainda aceitam balão acima/abaixo:
    // desloca o balão no eixo transversal em vez de cair no centro sobre o alvo.
    if (place === 'bottom') return { top: rect.bottom + gap, left: clampLeft(rect.left + rect.width / 2 - bw / 2), place };
    if (place === 'top') return { top: rect.top - bh - gap, left: clampLeft(rect.left + rect.width / 2 - bw / 2), place };
    if (place === 'right') return { top: clampTop(rect.top + rect.height / 2 - bh / 2), left: rect.right + gap, place };
    if (place === 'left') return { top: clampTop(rect.top + rect.height / 2 - bh / 2), left: rect.left - bw - gap, place };
    return null;
  };

  const order = [placement, 'bottom', 'top', 'right', 'left'];
  let chosen = null;
  for (const place of order) {
    const pos = tryPlace(place);
    if (!pos) continue;
    if (pos.top >= margin && pos.top + bh <= vh - margin - mobileNav
      && pos.left >= margin && pos.left + bw <= vw - margin) {
      chosen = pos;
      break;
    }
  }
  let top;
  let left;
  let place = 'center';
  if (chosen) {
    ({ top, left, place } = chosen);
  } else {
    top = Math.max(margin, (vh - bh - mobileNav) / 2);
    left = Math.max(margin, (vw - bw) / 2);
  }
  left = Math.min(Math.max(margin, left), Math.max(margin, vw - bw - margin));
  top = Math.min(Math.max(margin, top), Math.max(margin, vh - bh - margin - mobileNav));
  balloon.style.top = `${Math.round(top)}px`;
  balloon.style.left = `${Math.round(left)}px`;
  balloon.dataset.place = place;

  // Bico apontando para o centro do alvo, preso às bordas do balão.
  if (rect && place !== 'center') {
    if (place === 'bottom' || place === 'top') {
      const cx = rect.left + rect.width / 2 - left;
      beak.style.left = `${Math.round(Math.min(Math.max(16, cx), bw - 16))}px`;
      beak.style.top = '';
    } else {
      const cy = rect.top + rect.height / 2 - top;
      beak.style.top = `${Math.round(Math.min(Math.max(16, cy), bh - 16))}px`;
      beak.style.left = '';
    }
  }
}

function clearHatch() {
  if (hatchTimer) clearTimeout(hatchTimer);
  hatchTimer = 0;
}

function showNextButton(show) {
  nextBtn.classList.toggle('hidden', !show);
  nextBtn.disabled = !show;
}

function paintCopy(step) {
  const state = stateTutorial();
  const progress = phaseProgress(state.tutorial, step.phase);
  titleEl.textContent = hooks.t(step.titleKey);
  bodyEl.textContent = hooks.t(step.bodyKey);
  progressEl.textContent = hooks.t('tut.progress', {
    current: progress.currentIndex,
    total: progress.total,
  });
  const pct = progress.total ? Math.round((progress.currentIndex / progress.total) * 100) : 0;
  trackFill.style.width = `${pct}%`;
  skipBtn.textContent = hooks.t('tut.skip');
  nextBtn.textContent = hooks.t('tut.next');
  showNextButton(step.advance?.type === 'next' || step.advance?.type === 'any');
}

function showOverlay() {
  ensureDom();
  root.classList.remove('hidden');
  root.setAttribute('aria-hidden', 'false');
  root.style.zIndex = String(Z_INDEX);
}

function hideOverlay() {
  if (!root) return;
  root.classList.add('hidden');
  root.setAttribute('aria-hidden', 'true');
  closeSkipConfirm();
}

function stopTracking() {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = 0;
}

function startTracking() {
  stopTracking();
  if (document.hidden) return;
  const tick = () => {
    rafId = 0;
    if (!activeStep || document.hidden) return;
    if (activeStep.target) {
      const el = activeTarget && document.contains(activeTarget)
        ? activeTarget
        : resolveTarget(activeStep);
      if (!el) {
        // Esperando o alvo: NÃO bloqueia o jogo.
        if (!waitingSince) waitingSince = performance.now();
        // Passo que espera um evento do jogo não tem prazo: o jogador pode levar
        // minutos montando a caçada. Só desiste quem precisa do alvo agora.
        const semPrazo = activeStep.advance?.type === 'event';
        if (!semPrazo && performance.now() - waitingSince > TARGET_WAIT_MS) {
          discardActiveStep('alvo não apareceu');
          return;
        }
        clearSpotlightHole('waiting');
      } else {
        waitingSince = 0;
        if (activeTarget !== el) {
          activeTarget = el;
          lastRectKey = '';
          lastScrollAt = 0;
          scrollTargetIntoView(el);
        }
        let rect = el.getBoundingClientRect();
        if (isOffscreen(rect)) {
          scrollTargetIntoView(el);
          rect = el.getBoundingClientRect();
        }
        if (isOffscreen(rect)) {
          goneSince = 0;
          clearSpotlightHole('waiting');
        } else if (rect.width < 2 || rect.height < 2) {
          // "Ainda não apareceu" (HUD de combate antes de engajar) é diferente de
          // "apareceu e sumiu" (painel fechado). O primeiro espera, o segundo desiste.
          if (!paintedOnce) {
            if (!waitingSince) waitingSince = performance.now();
            const semPrazo = activeStep.advance?.type === 'event';
            if (!semPrazo && performance.now() - waitingSince > TARGET_WAIT_MS) {
              discardActiveStep('alvo nunca ficou visível');
              return;
            }
            clearSpotlightHole('waiting');
          } else {
            if (!goneSince) goneSince = performance.now();
            clearSpotlightHole('waiting');
            if (performance.now() - goneSince > TARGET_GONE_MS) {
              discardActiveStep('alvo sumiu');
              return;
            }
          }
        } else if (isOccluded(el, rect)) {
          // O alvo existe, só está atrás de outra camada (um modal aberto por
          // cima). Espera sem prazo: some a camada, o holofote assume. Descartar
          // aqui derrubava passos válidos enquanto o Atlas ainda fechava.
          goneSince = 0;
          clearSpotlightHole('waiting');
        } else {
          goneSince = 0;
          const key = rectKey(rect);
          if (key !== lastRectKey) {
            lastRectKey = key;
            applySpotlight(rect);
          }
        }
      }
    } else {
      clearSpotlightHole('modal');
    }
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);
}

/** Passo impossível agora: some da sessão, mas volta num próximo login. */
function discardActiveStep(motivo) {
  if (!activeStep) return;
  console.debug('[tutorial] passo descartado nesta sessão:', activeStep.id, motivo);
  sessionSkipped.add(activeStep.id);
  finishStep(false);
}

function completeActiveStep() {
  if (!activeStep) return;
  finishStep(true);
}

function finishStep(markDone) {
  const stepId = activeStep?.id;
  clearHatch();
  stopTracking();
  hideOverlay();
  if (markDone && stepId) {
    const state = stateTutorial();
    let tutorial = markStepDone(state.tutorial, stepId);
    for (const id of skippedByLayout({ ...state, tutorial })) {
      tutorial = markStepDone(tutorial, id);
    }
    tutorial = setCurrentStep(tutorial, null);
    persist(tutorial);
  } else {
    persist(setCurrentStep(stateTutorial().tutorial, null));
  }
  activeStep = null;
  activeTarget = null;
  lastRectKey = '';
  waitingSince = 0;
  goneSince = 0;
  scheduleAdvance();
}

function openStep(step) {
  if (!step) {
    hideOverlay();
    return;
  }
  try { hooks.prepareStep?.(step.id); } catch (error) {
    console.debug('[tutorial] prepareStep falhou', step.id, error);
  }
  ensureDom();
  activeStep = step;
  activeTarget = null;
  lastRectKey = '';
  waitingSince = performance.now();
  goneSince = 0;
  lastScrollAt = 0;
  firstPaint = true;
  paintedOnce = false;
  persist(setCurrentStep(stateTutorial().tutorial, step.id));
  paintCopy(step);
  clearSpotlightHole(step.target ? 'waiting' : 'modal');
  showOverlay();
  startTracking();

  // Saída de emergência: nenhum passo pode prender o jogador.
  clearHatch();
  const advance = step.advance?.type;
  if (advance === 'click' || advance === 'event') {
    hatchTimer = setTimeout(() => {
      if (activeStep?.id === step.id) showNextButton(true);
    }, ESCAPE_HATCH_MS);
  }

  queueMicrotask(() => {
    const focus = !nextBtn.classList.contains('hidden') ? nextBtn : skipBtn;
    focus?.focus({ preventScroll: true });
  });
}

function scheduleAdvance() {
  if (queued) return;
  queued = true;
  queueMicrotask(() => {
    queued = false;
    tryShowNext();
  });
}

function retryLater(ms = 500) {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => { retryTimer = 0; tryShowNext(); }, ms);
}

function tryShowNext() {
  if (!hooks) return;
  if (hooks.isBusy()) {
    retryLater(400);
    return;
  }
  const state = stateTutorial();
  let tutorial = state.tutorial;
  const layoutSkip = skippedByLayout(state);
  if (layoutSkip.length) {
    for (const id of layoutSkip) tutorial = markStepDone(tutorial, id);
    persist(tutorial);
  }
  const step = nextStep(hooks.getState(), undefined, undefined, sessionSkipped);
  if (!step) {
    hideOverlay();
    activeStep = null;
    return;
  }
  if (activeStep?.id === step.id && !root?.classList.contains('hidden')) return;
  // Contexto indisponível agora (ex.: caçando e o passo é na cidade): espera.
  if (!ensureContext(step)) {
    hideOverlay();
    activeStep = null;
    return;
  }
  openStep(step);
}

/**
 * Inicializa o tutorial. Preview mode: o caller não deve chamar.
 */
export function initTutorial(nextHooks) {
  hooks = nextHooks;
  ensureDom();
  bindVisibility();
  bindClickAdvance();

  const state = stateTutorial();
  if (shouldMigrateVeteran(state)) {
    persist(markAllPhasesDone(state.tutorial));
    try { hooks.showToast(hooks.t('tut.veteranToast')); } catch { /* ignore */ }
    return;
  }

  const leveled = maybeStartNextPhase(state.tutorial, state, null, {});
  if (leveled.phase !== state.tutorial.phase) persist(leveled);

  scheduleAdvance();
}

/** Emite um evento do jogo para o tutorial (gatilhos de fase / avanço por event). */
export function tutorialEvent(name, payload = {}) {
  if (!hooks) return;
  const state = stateTutorial();
  if (state.tutorial.skipped) return;

  let tutorial = state.tutorial;
  const opened = maybeStartNextPhase(tutorial, state, name, payload);
  if (opened.phase !== tutorial.phase || opened.startedAt !== tutorial.startedAt || opened.current !== tutorial.current) {
    persist(opened);
    tutorial = opened;
  }

  if (activeStep?.advance?.type === 'event' && activeStep.advance.name === name) {
    completeActiveStep();
    return;
  }

  scheduleAdvance();
}

export function restartTutorial() {
  if (!hooks) return;
  sessionSkipped.clear();
  persist(restartTutorialProgress(stateTutorial().tutorial));
  activeStep = null;
  hideOverlay();
  scheduleAdvance();
}

export function isTutorialActive() {
  return !!activeStep && !root?.classList.contains('hidden');
}

/** Id do passo em exibição (o app usa para não fechar painéis sob o holofote). */
export function activeTutorialStepId() {
  return isTutorialActive() ? activeStep.id : null;
}
